import { apiErrorMessage, customFetch } from '@agos/api-client'
import { getGetStockItemsQueryKey, usePostStockReceipts } from '@agos/api-client/inventory'
import { T, formatCents, tt } from '@agos/shared'
import { Spinner, cn, toast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { FileUp, X } from 'lucide-react'
import { useState } from 'react'

import {
  EMPTY_RECEIPT,
  fileProblem,
  matchAll,
  matchNote,
  ocrLinesOf,
  ocrUnavailableNote,
  receiptPayload,
  receiptProblems,
  totalNote,
  type MatchedLine,
  type ReceiptForm,
  type StockItemLike,
  matchAllParsed,
  pdfLinesOf,
  readerFor,
  readerNote,
} from './goods-receipt'

/**
 * Recording a delivery, optionally read off a photograph.
 *
 * The OCR answers names and the receipt endpoint needs ids, so the matching is on screen: a line nobody
 * could match is shown unmatched rather than dropped or guessed, and a name that is three stock items —
 * *Golden Múka T650* is, in this data — asks which. See `goods-receipt.ts`.
 *
 * On this stack the upload answers **503**, because no AI key is configured; the dialog says so and
 * points at manual entry rather than leaving somebody to blame their photograph.
 */
export function GoodsReceiptDialog({
  items,
  onClose,
  onSaved,
}: {
  items: readonly StockItemLike[]
  onClose: () => void
  onSaved: () => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ReceiptForm>(EMPTY_RECEIPT)
  const [matched, setMatched] = useState<MatchedLine[]>([])
  const [reading, setReading] = useState(false)
  /** Which reader produced the rows on screen — §9.135. */
  const [readNote, setReadNote] = useState<string | null>(null)
  const [ocrError, setOcrError] = useState<string | null>(null)

  const save = usePostStockReceipts({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetStockItemsQueryKey() })
        onSaved()
        onClose()
        toast({ title: tt('Príjemka zapísaná') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Príjemku sa nepodarilo zapísať'),
          description: apiErrorMessage(caught, ''),
        }),
    },
  })

  /**
   * The upload is multipart, so it goes through `customFetch` directly rather than the generated hook —
   * which would `JSON.stringify` the file. The wrapper still carries the auth and tenant headers (§6).
   */
  const readPhotograph = async (file: File) => {
    const problem = fileProblem(file)
    if (problem !== null) {
      setOcrError(problem)
      return
    }
    setOcrError(null)
    setReadNote(null)
    setReading(true)
    const reader = readerFor(file)
    try {
      // §9.135 — a PDF goes to the local parser, a photograph to the vision model. The parser reads
      // text the supplier wrote and costs nothing; the model guesses at pixels and is metered.
      const reader = readerFor(file)
      const body = new FormData()
      if (reader === 'pdf') {
        body.append('pdf', file)
        const answer = await customFetch<unknown>('/api/v1/invoices/parse-pdf', {
          method: 'POST',
          body,
        })
        const lines = pdfLinesOf(answer)
        // An empty read leaves whatever was typed by hand alone — losing it to a parser that found
        // nothing is worse than the parser finding nothing.
        if (lines.length === 0) {
          setOcrError(tt('V PDF sa nenašli žiadne položky - zapíšte ich ručne nižšie.'))
          return
        }
        setMatched(matchAllParsed(lines, items))
        setReadNote(readerNote('pdf', lines.length))
        return
      }
      body.append('file', file)
      const answer = await customFetch<unknown>('/api/v1/stock/receipts/ocr', { method: 'POST', body })
      const lines = ocrLinesOf(answer)
      if (lines.length === 0) {
        setOcrError(tt('Na fotke sa nenašli žiadne položky - zapíšte ich ručne nižšie.'))
        return
      }
      setMatched(matchAll(lines, items))
      setReadNote(readerNote('photo', lines.length))
    } catch (caught) {
      setOcrError(apiErrorMessage(caught, reader === 'pdf' ? tt('PDF sa nepodarilo prečítať.') : ocrUnavailableNote()))
    } finally {
      setReading(false)
    }
  }

  const problems = receiptProblems(form, matched)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-receipt-dialog>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label={tt('Zavrieť')}
        onClick={onClose}
      />
      <section className="relative w-full max-w-2xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-background border border-border p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">
            <T>Príjemka</T>
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zavrieť')}
            data-receipt-close
            className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-xl hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <input
            value={form.invoiceNumber}
            onChange={(event) => setForm((current) => ({ ...current, invoiceNumber: event.target.value }))}
            placeholder={tt('Číslo faktúry')}
            aria-label={tt('Číslo faktúry')}
            data-receipt-invoice
            className="min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
          />
          <input
            value={form.supplier}
            onChange={(event) => setForm((current) => ({ ...current, supplier: event.target.value }))}
            placeholder={tt('Dodávateľ')}
            aria-label={tt('Dodávateľ')}
            data-receipt-supplier
            className="min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
          />
          <input
            type="date"
            value={form.date}
            onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
            aria-label={tt('Dátum')}
            data-receipt-date
            className="min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <FileUp className="h-4 w-4 text-primary" />
          <T>Načítať z fotky faktúry</T>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            data-receipt-photo
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file !== undefined) void readPhotograph(file)
            }}
            className="text-xs"
          />
          {reading && <Spinner />}
        </label>
        {ocrError !== null && (
          <p className="text-[11px] text-amber-500" data-ocr-error>
            {ocrError}
          </p>
        )}

        {matched.length > 0 && (
          <>
            {/* §9.135 - which reader produced these. The parser reads text the supplier wrote and
              * the model guesses at pixels; somebody checking against paper is entitled to know. */}
            {readNote !== null && (
              <p className="text-[11px] font-semibold text-primary" data-read-note>
                {readNote}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground" data-match-note>
              {matchNote(matched)}
            </p>
            <ul className="space-y-1.5">
              {matched.map((row, index) => (
                <li
                  key={`${row.line.name}-${index}`}
                  className="flex items-center gap-2 text-xs"
                  data-receipt-line={row.kind}
                >
                  <span className="flex-1 truncate">
                    {row.line.qty} {row.line.unit} · {row.line.name}
                  </span>
                  <span className="tabular-nums">{formatCents(row.line.unitPriceCents)}</span>
                  <select
                    value={row.stockItemId === null ? '' : String(row.stockItemId)}
                    onChange={(event) =>
                      setMatched((current) =>
                        current.map((candidate, position) =>
                          position === index
                            ? {
                                ...candidate,
                                stockItemId:
                                  event.target.value === '' ? null : Number(event.target.value),
                              }
                            : candidate,
                        ),
                      )
                    }
                    aria-label={row.line.name}
                    data-receipt-item={index}
                    className={cn(
                      'min-h-[36px] rounded-lg bg-card border px-2 text-xs max-w-[14rem]',
                      row.stockItemId === null ? 'border-amber-500' : 'border-border',
                    )}
                  >
                    <option value="">{tt('- vynechať -')}</option>
                    {(row.candidates.length > 0 ? row.candidates : items).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
            <p className="text-xs font-bold" data-receipt-total>
              {totalNote(matched)}
            </p>
          </>
        )}

        {problems.map((problem) => (
          <p key={problem} className="text-[11px] text-destructive" data-receipt-problem>
            {problem}
          </p>
        ))}

        <button
          type="button"
          data-receipt-save
          disabled={problems.length > 0 || save.isPending}
          onClick={() => save.mutate({ data: receiptPayload(form, matched) })}
          className={cn('w-full min-h-[48px] rounded-xl', BUTTON_PRIMARY)}
        >
          {save.isPending ? <Spinner /> : <T>Zapísať príjemku</T>}
        </button>
      </section>
    </div>
  )
}
